/**
 * Combos admin page (/combos, super_admin only). Phase 2 of the APC
 * Commercial Engine. Create virtual products made of multiple catalog items.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, Package, X, Image as ImageIcon, CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/uploadPhoto";

const EMPTY = {
  name: "", description: "", price: "", currency: "MXN",
  category: "", badge_label: "COMBO", reserved_count: 0, active: true,
  photos: [], members: [], free_shipping: true,
};

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Combos() {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: ps }] = await Promise.all([
        api.get("/combos"), api.get("/products"),
      ]);
      setCombos(cs); setProducts(ps);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name, description: c.description || "", price: c.price,
      currency: c.currency || "MXN", category: c.category || "",
      badge_label: c.badge_label || "COMBO",
      reserved_count: c.reserved_count || 0, active: c.active,
      photos: c.photos || [],
      members: (c.members || []).map((m) => ({ product_id: m.product_id, qty: m.qty })),
      free_shipping: c.free_shipping !== false,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (!form.price || Number(form.price) <= 0) return toast.error("Precio inválido");
    if (form.members.length === 0) return toast.error("Agrega al menos un producto integrante");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        reserved_count: Number(form.reserved_count || 0),
        photos: form.photos.filter(Boolean),
        members: form.members.map((m) => ({ product_id: m.product_id, qty: Number(m.qty || 1) })),
      };
      if (editingId) await api.patch(`/combos/${editingId}`, payload);
      else await api.post("/combos", payload);
      toast.success(editingId ? "Combo actualizado" : "Combo creado");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"?`)) return;
    try {
      await api.delete(`/combos/${c.id}`);
      toast.success("Eliminado"); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/combos/${c.id}`, { active: !c.active });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máx 8 MB");
    try {
      const url = await uploadPhoto(file, { folder: "alendrapc/combos", maxSide: 1600, quality: 0.85 });
      setForm((f) => ({ ...f, photos: [...f.photos, url] }));
    } catch (err) { toast.error(err?.message || "No se pudo subir"); }
  };

  const addMember = (pid) => {
    if (form.members.some((m) => m.product_id === pid)) return;
    setForm((f) => ({ ...f, members: [...f.members, { product_id: pid, qty: 1 }] }));
    setProductSearch("");
  };
  const updateMemberQty = (pid, qty) => setForm((f) => ({
    ...f, members: f.members.map((m) => m.product_id === pid ? { ...m, qty: Math.max(1, Number(qty) || 1) } : m),
  }));
  const removeMember = (pid) => setForm((f) => ({ ...f, members: f.members.filter((m) => m.product_id !== pid) }));

  const filteredProducts = productSearch.trim()
    ? products.filter((p) => !form.members.some((m) => m.product_id === p.id) && (p.name + " " + (p.brand || "") + " " + (p.sku || "")).toLowerCase().includes(productSearch.toLowerCase())).slice(0, 10)
    : [];

  const calcSum = () => form.members.reduce((s, m) => {
    const p = products.find((x) => x.id === m.product_id);
    if (!p) return s;
    return s + (Number(p.store_price) || 0) * Number(m.qty || 1);
  }, 0);
  const sumOrig = calcSum();
  const savings = Math.max(0, sumOrig - Number(form.price || 0));

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MOTOR COMERCIAL · FASE 2</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <Package size={32} weight="duotone" className="text-[#7E57C2]" />
            Combos
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{combos.length} combos · {combos.filter((c) => c.active && c.availability?.available > 0).length} disponibles en tienda</p>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-[#7E57C2] hover:bg-[#5E37A2] text-white" data-testid="create-combo">
          <Plus size={14} weight="bold" /> Nuevo combo
        </Button>
      </header>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Combo</th>
              <th className="text-left px-4 py-3">Productos</th>
              <th className="text-left px-4 py-3">Suma orig.</th>
              <th className="text-left px-4 py-3">Precio</th>
              <th className="text-left px-4 py-3">Ahorro</th>
              <th className="text-left px-4 py-3">Disponibles</th>
              <th className="text-left px-4 py-3">Vendidos</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>}
            {!loading && combos.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin combos. Crea el primero.</td></tr>
            )}
            {!loading && combos.map((c) => {
              const av = c.availability || {};
              return (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`combo-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{c.name}</div>
                    {c.badge_label && <span className="inline-block mt-0.5 px-2 py-0.5 text-[10px] font-bold rounded bg-[#7E57C2]/10 text-[#7E57C2]">{c.badge_label}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-700">
                    {(c.members || []).map((m, i) => {
                      const md = (av.members_detail || []).find((d) => d.product_id === m.product_id);
                      return <div key={i}>{m.qty}× {md?.name || "(eliminado)"}</div>;
                    })}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 line-through numeric">{fmtMXN(c.original_total)}</td>
                  <td className="px-4 py-3 font-bold text-[#7E57C2] numeric">{fmtMXN(c.price)}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{fmtMXN(c.savings)}</span></td>
                  <td className="px-4 py-3 font-bold numeric">{av.available}</td>
                  <td className="px-4 py-3 text-zinc-500 numeric">{c.sold_count || 0}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(c)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                      {c.active ? <><CheckCircle size={11} weight="fill" /> Activo</> : <><XCircle size={11} weight="fill" /> Inactivo</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-zinc-100 rounded"><PencilSimple size={14} weight="bold" /></button>
                    <button onClick={() => remove(c)} className="p-1.5 hover:bg-red-50 text-red-600 rounded ml-1"><Trash size={14} weight="bold" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingId ? "Editar combo" : "Nuevo combo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Combo Oficina Básica" className="mt-1" data-testid="combo-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Badge</Label>
                <Input value={form.badge_label} onChange={(e) => setForm({ ...form, badge_label: e.target.value.toUpperCase() })} placeholder="COMBO · OFICINA" maxLength={24} className="mt-1 font-bold" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1 resize-none" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Precio *</Label>
                <Input type="number" min="0" step="50" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 numeric font-bold text-[#7E57C2]" data-testid="combo-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda</Label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 w-full h-9 border border-zinc-300 rounded px-2 text-sm">
                  <option value="MXN">MXN</option><option value="USD">USD</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Reservados</Label>
                <Input type="number" min="0" value={form.reserved_count} onChange={(e) => setForm({ ...form, reserved_count: e.target.value })} className="mt-1 numeric" />
                <p className="text-[10px] text-zinc-500 mt-1">0 = ilimitado (solo por stock).</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Oficina, Gamer…" className="mt-1" />
              </div>
            </div>

            {/* Members */}
            <div>
              <Label className="text-xs uppercase tracking-wider">Productos integrantes *</Label>
              <div className="mt-1 border border-zinc-200 rounded p-3 bg-zinc-50 space-y-2">
                {form.members.length === 0 && <div className="text-xs text-zinc-400">Aún no agregas productos</div>}
                {form.members.map((m) => {
                  const p = products.find((x) => x.id === m.product_id);
                  return (
                    <div key={m.product_id} className="flex items-center gap-2 bg-white p-2 rounded border border-zinc-200">
                      <div className="flex-1 text-xs">
                        <div className="font-semibold">{p?.name || "(eliminado)"}</div>
                        <div className="text-zinc-500">{p?.brand} · stock {p?.stock} · {fmtMXN(p?.store_price)}</div>
                      </div>
                      <Input type="number" min="1" value={m.qty} onChange={(e) => updateMemberQty(m.product_id, e.target.value)} className="w-16 text-xs numeric" />
                      <button onClick={() => removeMember(m.product_id)} className="text-zinc-400 hover:text-red-600"><X size={14} weight="bold" /></button>
                    </div>
                  );
                })}
                <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Buscar producto para agregar…" className="text-xs" />
                {filteredProducts.length > 0 && (
                  <div className="max-h-32 overflow-y-auto bg-white rounded border border-zinc-200">
                    {filteredProducts.map((p) => (
                      <button key={p.id} type="button" onClick={() => addMember(p.id)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b last:border-0 border-zinc-100">
                        <span className="font-semibold">{p.name}</span> · {fmtMXN(p.store_price)} · stock {p.stock}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {sumOrig > 0 && (
                <div className="mt-2 text-xs text-zinc-600">
                  Suma original: <span className="numeric font-semibold">{fmtMXN(sumOrig)}</span>
                  {savings > 0 && <span className="ml-2 text-emerald-700 font-bold">Ahorro: {fmtMXN(savings)}</span>}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><ImageIcon size={12} weight="bold" /> Fotos del combo</Label>
              <div className="flex flex-wrap gap-2 mb-2 mt-1">
                {form.photos.map((url, i) => (
                  <div key={i} className="relative w-20 h-20 border border-zinc-200 rounded overflow-hidden group">
                    <img src={url} alt="" className="w-full h-full object-contain" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 inline-flex items-center justify-center"><X size={10} /></button>
                  </div>
                ))}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => addPhoto(e.target.files?.[0])} className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#7E57C2] file:text-white file:rounded file:cursor-pointer" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm">Combo activo (visible en la tienda si hay disponibilidad)</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer p-2.5 rounded-md border border-emerald-200 bg-emerald-50/40">
              <input
                type="checkbox"
                checked={!!form.free_shipping}
                onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })}
                className="w-4 h-4 accent-emerald-600"
                data-testid="combo-free-shipping"
              />
              <span className="text-sm">
                <span className="text-emerald-700 font-semibold">🚚 Envío gratis</span>
                <span className="text-zinc-500"> — Se mostrará un badge "Envío gratis" en la tienda.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#7E57C2] hover:bg-[#5E37A2] text-white" data-testid="save-combo">
              {saving ? "Guardando…" : editingId ? "Guardar" : "Crear combo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
